import { t } from "@/context/language";
import { cn } from "@/utils";
import {
  AnimatePresence,
  motion,
  useInView,
  useReducedMotion,
} from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { SOLUTION_CHATS } from "../constants/solutionChats";
import { ChatPreviewCard } from "./chatPreviewCard";

const VISIBLE = 3;
const TICK_MS = 3000;
// Degrau horizontal da cascata. É margem, não `x`: a animação de layout do
// framer-motion acompanha margem, mas sobrescreve um `x` animado à parte.
const STEP_CLASS = ["ml-0", "ml-9 sm:ml-12", "ml-18 sm:ml-24"];

const chatAt = (index: number) => SOLUTION_CHATS[index % SOLUTION_CHATS.length];

export function SolutionChatsCard() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { margin: "-80px" });
  const reduceMotion = useReducedMotion();

  // `tick` conta quantas mensagens já chegaram; a da frente é a mais nova.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!inView || reduceMotion) return;
    const id = window.setInterval(() => setTick(prev => prev + 1), TICK_MS);
    return () => window.clearInterval(id);
  }, [inView, reduceMotion]);

  // Posição 0 é o fundo da pilha (a mais antiga); a última é a da frente.
  const cards = Array.from({ length: VISIBLE }, (_, position) => {
    const index = tick + position;
    return { index, chat: chatAt(index) };
  });

  return (
    <figure ref={ref} className="w-full max-w-md">
      <figcaption className="sr-only">
        {t.solutions.chatsCard.caption}
      </figcaption>

      {/* Cada card sobe por cima do anterior (`-mt-16`) e anda um degrau para a
          direita: a cascata da arte original. */}
      <div className="flex flex-col" aria-hidden="true">
        <AnimatePresence initial={false} mode="popLayout">
          {cards.map(({ index, chat }, position) => (
            <motion.div
              key={index}
              layout={!reduceMotion}
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              style={{ zIndex: position + 1 }}
              className={cn(
                "relative w-3/4 not-first:-mt-16",
                STEP_CLASS[position]
              )}
            >
              <ChatPreviewCard chat={chat} />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </figure>
  );
}
